import { AsyncPipe, DOCUMENT } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  State,
  getAllStyles,
  getExhibitionStyles,
  getLanguage,
  getOperatorsSettings,
  getSelectedExhibition,
  getSelectedExhibitionId,
  getSelectedStyle,
  getStyleId
} from '@app/app.reducer';
import {
  CreateStyle,
  GetAllStyles,
  LoadStyle,
  SaveEventStyles,
  SetSelectedStyle,
  UpdateStyle
} from '@app/colorizer/colorizer.actions';
import { ColorizerModalTypes, StyleTemplateModel } from '@app/colorizer/colorizer.interface';
import { LsButtonComponent, LsModalComponent, LsTemplateDirective } from '@axess/ng-limestone';
import { injectStyleElement } from '@core/utils/dom-helpers';
import { Store, select } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HideTranslationPipe } from '@shared/pipes/hide-translation/hide-translation.pipe';
import { KeysPipe } from '@shared/pipes/keys/keys.pipe';
import themeDefault from '@src/assets/admin/theme-default.json';
import { CssSkinModel, StyleListKey } from '@store/customization/customization.interfaces';
import { CustomizationService } from '@store/customization/customization.service';
import { generateSkinCss, styleList } from '@store/customization/customization.styles';
import { AddAttributes } from '@ui/directives/add-attributes';
import { isNil, omitBy } from 'lodash';
import { ColorPickerModule } from 'ngx-color-picker';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { debounceTime, filter, first, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-colorizer',
  templateUrl: './colorizer.component.html',
  styleUrls: ['./colorizer.component.scss'],
  standalone: true,
  imports: [
    ColorPickerModule,
    FormsModule,
    TranslateModule,
    HideTranslationPipe,
    AsyncPipe,
    KeysPipe,
    LsTemplateDirective,
    AddAttributes,
    LsModalComponent,
    LsButtonComponent
  ]
})
export class ColorizerComponent implements OnInit, OnDestroy {
  readonly #document = inject(DOCUMENT);

  styleList = styleList;
  allSkinVariables: any;
  expanded = false;
  modalWindowOpen = false;
  modalWindowType = '';
  selectedTemplateID: number;
  allStyleTemplates$: Observable<StyleTemplateModel[]>;
  modalTitle = '';
  templateTitle = '';
  activeLang: string;
  selectedTemplateTitle: string;
  eventType: string;
  translationEventTitle = '';
  preselectedTemplate: StyleTemplateModel = null;
  readonly ColorizerModalTypes = ColorizerModalTypes;
  private subscriptions = new Subscription();

  constructor(
    private store: Store<State>,
    private customizationService: CustomizationService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.activeLang = this.translateService.currentLang;
    this.store.dispatch(new GetAllStyles());

    this.subscriptions.add(
      this.store.pipe(select(getStyleId)).subscribe((styleId: number) => {
        this.selectedTemplateID = styleId;
      })
    );

    this.allStyleTemplates$ = this.store.select(getAllStyles);

    this.subscriptions.add(
      this.store.pipe(select(getSelectedExhibition)).subscribe((event) => {
        if (event && event !== null) {
          this.translationEventTitle = event.eventName;
        } else {
          this.translationEventTitle = 'operator';
        }
      })
    );

    this.subscriptions.add(
      this.store.pipe(select(getSelectedExhibitionId)).subscribe((eventId) => {
        if (eventId !== null) {
          this.eventType = 'event';
        } else {
          this.eventType = 'operator';
        }
      })
    );

    this.subscriptions.add(
      this.store.pipe(select(getSelectedStyle)).subscribe((skin: CssSkinModel) => {
        if (skin && skin.variables) {
          const allVariables = this.generateSkinVariables(skin);
          this.allSkinVariables = allVariables;
          this.customizationService.appendSkinCSS(this.allSkinVariables, 5, false, this.selectedTemplateID);
        }
      })
    );

    // when we load the colorizer tool, check which style is active
    this.subscriptions.add(
      combineLatest([
        this.store.pipe(select(getExhibitionStyles)),
        this.store.pipe(select(getAllStyles)),
        this.store.pipe(select(getOperatorsSettings))
      ])
        .pipe(
          filter(([exhibitionStyles, styleTemplates, operatorSettings]) => {
            return (
              !!exhibitionStyles &&
              Object.hasOwn(exhibitionStyles, 'variables') &&
              !!styleTemplates &&
              !!operatorSettings
            );
          }),
          debounceTime(100)
        )
        .subscribe(([exhibitionStyles, styleTemplates, operatorSettings]) => {
          // set the variables of currently selected template into inputs
          this.allSkinVariables = this.generateSkinVariables(exhibitionStyles);

          if (Object.hasOwn(exhibitionStyles, 'id') && (exhibitionStyles.id || exhibitionStyles.id === 0)) {
            this.selectedTemplateID = exhibitionStyles.id;

            styleTemplates.forEach((styleTemplate) => {
              if (this.selectedTemplateID === styleTemplate.id) {
                this.selectedTemplateTitle = styleTemplate.title;
              }
            });
          } else if (operatorSettings.customStylesId) {
            this.selectedTemplateID = operatorSettings.customStylesId;

            styleTemplates.forEach((styleTemplate) => {
              if (this.selectedTemplateID === styleTemplate.id) {
                this.selectedTemplateTitle = styleTemplate.title;
              }
            });
          } else {
            console.log('id not present');
          }
        })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  togglePicker() {
    const designToolWrapper = document.querySelector('.template-style-list');
    designToolWrapper.classList.toggle('no-scroll');
  }

  inputChanged(styleVariableKey: StyleListKey, value) {
    this.allSkinVariables[styleVariableKey] = value;
    this.generateAndApplyStyles();
  }

  generateAndApplyStyles() {
    if (this.allSkinVariables) {
      // add styles
      const cssString = generateSkinCss(this.allSkinVariables);
      const cssSkin: CssSkinModel = {
        priority: 5,
        css: cssString,
        variables: this.allSkinVariables
      };

      this.store.dispatch(new SetSelectedStyle(cssSkin));

      this.customizationService.appendSkinCSS(this.allSkinVariables, 5, false, this.selectedTemplateID);
    }
  }

  mouseOver(styleListKey: StyleListKey) {
    const css = this.generateHighlightCss(styleListKey);

    injectStyleElement(this.#document, css, 'adminHighlight');
  }

  mouseLeave() {
    injectStyleElement(this.#document, '', 'adminHighlight');
  }

  generateSkinVariables(skin: CssSkinModel) {
    const allVariables = {};

    Object.keys(styleList).forEach((styleKey) => {
      const containsVariables = skin && skin.variables;
      const fallbackStyle = themeDefault[styleKey] || null;

      if (containsVariables) {
        allVariables[styleKey] = skin.variables[styleKey] || fallbackStyle;
      } else {
        allVariables[styleKey] = fallbackStyle;
      }
    });

    return allVariables;
  }

  generateHighlightCss(styleListKey: StyleListKey) {
    let cssString = '';
    if (Object.hasOwn(styleList, styleListKey)) {
      cssString = styleList[styleListKey].list.reduce((allStyles, currentSelector) => {
        return (
          allStyles +
          `${currentSelector.selector} {
  outline: 1px dashed black !important;
}
`
        );
      }, cssString);
    }

    return cssString;
  }

  checkIfEmpty($event, variableKey) {
    if (!$event.target.value) {
      this.inputChanged(variableKey, '');
    }
  }

  openModalWindow(type: string) {
    this.modalWindowType = type;
    this.modalWindowOpen = true;
    switch (type) {
      case 'load':
        this.translateTitle(type, '');
        break;

      case 'update':
        this.translateTitle(type, '');
        break;

      case 'create':
        this.translateTitle(type, '');
        break;

      case 'save':
        this.translateTitle(type, `.${this.eventType}`);
        break;

      default:
        break;
    }
  }

  translateTitle(type, eventType) {
    this.store
      .pipe(
        select(getLanguage),
        switchMap((currentLang) => {
          return this.translateService.get(`modalWindow.colorizer.${type}-title${eventType}`, {
            lang: currentLang.toUpperCase()
          });
        })
      )
      .subscribe((title: string) => {
        this.modalTitle = title;
      });
  }

  submitModalWindow($event) {
    $event.stopPropagation();
    this.modalWindowOpen = false;

    const templateID = this.selectedTemplateID;

    switch (this.modalWindowType) {
      case ColorizerModalTypes.Load:
        this.selectedTemplateID = this.preselectedTemplate.id;
        this.selectedTemplateTitle = this.preselectedTemplate.title;
        this.preselectedTemplate = null;
        this.store.dispatch(new LoadStyle(this.selectedTemplateID));
        break;

      case ColorizerModalTypes.Update:
        this.store.dispatch(
          new UpdateStyle({
            title: templateID != null ? this.selectedTemplateTitle : '* Default *',
            styles: omitBy(this.allSkinVariables, isNil) as StyleTemplateModel,
            styleId: templateID != null ? templateID : -1
          })
        );
        break;

      case ColorizerModalTypes.Save:
        this.store.pipe(select(getSelectedExhibitionId), first()).subscribe((eventId) => {
          eventId = eventId ? Number(eventId) : null;

          this.store.dispatch(
            new SaveEventStyles({
              styleId: this.selectedTemplateID,
              eventId: eventId
            })
          );
        });
        break;

      case ColorizerModalTypes.Create:
        this.store.dispatch(
          new CreateStyle({
            title: this.templateTitle,
            styles: omitBy(this.allSkinVariables, isNil) as StyleTemplateModel
          })
        );
        this.templateTitle = '';
        break;

      default:
        break;
    }
  }

  closeModalWindow($event) {
    $event.stopPropagation();
    this.modalWindowOpen = false;
    this.preselectedTemplate = null;
  }

  preselectTile($event: MouseEvent | KeyboardEvent, template: StyleTemplateModel) {
    $event.preventDefault();
    $event.stopPropagation();

    this.preselectedTemplate = template;
  }

  protected toggleIsOpen($event: MouseEvent | KeyboardEvent) {
    $event.preventDefault();
    $event.stopPropagation();

    this.expanded = !this.expanded;
  }
}
